import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_QUOTES = [
  {
    id: 1, request_id: 102, status: "submitted",
    amount: 85000, currency: "ARS",
    description: "Renovación completa de baño: demolición, instalación nuevas cañerías, sanitarios y terminaciones.",
    estimated_visit_required: true,
    estimated_start_date: "2026-04-05",
    valid_until: new Date(Date.now() + 20 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    service_key: "plomeria",
    client_name: "Roberto Fernández",
    request_status: "waiting_quotes",
    common_data: { address: "Chacabuco 890, San Telmo", description: "Renovación completa de baño" },
  },
  {
    id: 2, request_id: 98, status: "accepted",
    amount: 25000, currency: "ARS",
    description: "Reparación de pérdida en cañería principal + sellado de juntas.",
    estimated_visit_required: false,
    estimated_start_date: "2026-03-28",
    valid_until: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    service_key: "plomeria",
    client_name: "Sofía Ramírez",
    request_status: "assigned",
    common_data: { address: "Palermo Soho, CABA", description: "Pérdida en cañería" },
  },
  {
    id: 3, request_id: 95, status: "rejected",
    amount: 120000, currency: "ARS",
    description: "Instalación de termotanque solar + cañerías.",
    estimated_visit_required: true,
    estimated_start_date: "2026-04-10",
    valid_until: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    service_key: "plomeria",
    client_name: "Lucas Pérez",
    request_status: "assigned",
    common_data: { address: "Belgrano, CABA", description: "Instalación termotanque solar" },
  },
];

const STATUS_CONFIG = {
  submitted: { label: "Enviado",   color: "#b06a00", bg: "#fff3e0", icon: "📤" },
  viewed:    { label: "Visto",     color: "#163244", bg: "#e7f0f5", icon: "👁" },
  accepted:  { label: "Aceptado",  color: "#1b7f4e", bg: "#e8f7ef", icon: "✓" },
  rejected:  { label: "Rechazado", color: "#c0392b", bg: "#fdecea", icon: "✕" },
  expired:   { label: "Expirado",  color: "#7b5ea7", bg: "#f3eeff", icon: "⏰" },
};

const SERVICE_LABELS = {
  plomeria: "Plomería", electricidad: "Electricidad", gasista: "Gasista",
  pintura: "Pintura", albanileria: "Albañilería",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatARS(amount) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(amount);
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function remainingTime(dateStr) {
  const diff = new Date(dateStr) - Date.now();
  if (diff <= 0) return null;
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}min` : `${mins}min`;
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditModal({ quote, onSave, onClose }) {
  const [form, setForm] = useState({
    amount:                   quote.amount,
    description:              quote.description,
    estimated_visit_required: quote.estimated_visit_required,
    estimated_start_date:     quote.estimated_start_date || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.patch(`/matching/provider/quotes/${quote.id}`, {
        amount:                   form.amount,
        description:              form.description,
        estimated_visit_required: form.estimated_visit_required,
        estimated_start_date:     form.estimated_start_date || null,
      });
      onSave({ ...quote, ...form });
      onClose();
    } catch (err) {
      console.error("Error al actualizar presupuesto:", err);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: "10px 14px", borderRadius: 8, border: "1.5px solid #cddae4",
    fontSize: 14, fontFamily: "Montserrat, sans-serif", color: "#163244",
    background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, color: "#163244",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.5)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "32px",
        width: "min(520px, 92vw)",
        boxShadow: "0 8px 40px rgba(22,50,68,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <h3 style={{ margin: 0, color: "#163244", fontSize: 18, fontWeight: 800 }}>
            Editar presupuesto
          </h3>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20,
            color: "#8fa3b1", cursor: "pointer",
          }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Monto (ARS) *</label>
          <input
            type="number"
            style={inputStyle}
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))}
            min={0}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Descripción del trabajo *</label>
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: "vertical", lineHeight: 1.6 }}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Inicio estimado</label>
            <input
              type="date"
              style={inputStyle}
              value={form.estimated_start_date}
              onChange={e => setForm(f => ({ ...f, estimated_start_date: e.target.value }))}
            />
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>¿Requiere visita previa?</label>
            <div
              onClick={() => setForm(f => ({ ...f, estimated_visit_required: !f.estimated_visit_required }))}
              style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
              }}
            >
              <div style={{
                width: 44, height: 24, borderRadius: 12,
                background: form.estimated_visit_required ? "#163244" : "#c8d4dc",
                position: "relative", transition: "background 0.25s",
              }}>
                <div style={{
                  position: "absolute", top: 3,
                  left: form.estimated_visit_required ? 23 : 3,
                  width: 18, height: 18, borderRadius: "50%",
                  background: form.estimated_visit_required ? "#E7A432" : "#fff",
                  transition: "left 0.25s",
                }} />
              </div>
              <span style={{ fontSize: 13, color: "#163244", fontWeight: 600 }}>
                {form.estimated_visit_required ? "Sí" : "No"}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: "11px 22px", borderRadius: 8,
              border: "2px solid #e0e9ef", background: "transparent",
              color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: "11px 28px", borderRadius: 8,
              background: "#163244", border: "none", color: "#fff",
              fontFamily: "Montserrat, sans-serif", fontWeight: 700,
              fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {loading ? (
              <span style={{
                width: 14, height: 14, border: "2px solid #fff",
                borderTopColor: "transparent", borderRadius: "50%",
                animation: "spin 0.7s linear infinite", display: "inline-block",
              }} />
            ) : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quote Card ───────────────────────────────────────────────────────────────
function QuoteCard({ quote, onEdit }) {
  const navigate = useNavigate();
  const cfg      = STATUS_CONFIG[quote.status] || STATUS_CONFIG.submitted;
  const canEdit  = quote.status === "submitted";
  const remaining = remainingTime(quote.valid_until);

  return (
    <div style={{
      background: "#fff", borderRadius: 14, marginBottom: 14,
      border: `2px solid ${quote.status === "accepted" ? "#1b7f4e" : "#e0e9ef"}`,
      boxShadow: quote.status === "accepted"
        ? "0 4px 20px rgba(27,127,78,0.1)"
        : "0 2px 10px rgba(22,50,68,0.06)",
      overflow: "hidden",
    }}>
      {/* Status strip */}
      <div style={{
        background: cfg.bg, padding: "6px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${cfg.color}22`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>{cfg.icon}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, letterSpacing: "0.05em" }}>
            {cfg.label.toUpperCase()}
          </span>
        </div>
        {canEdit && remaining && (
          <span style={{ fontSize: 11, color: "#b06a00", fontWeight: 600 }}>
            Válido por {remaining}
          </span>
        )}
        {!remaining && canEdit && (
          <span style={{ fontSize: 11, color: "#c0392b", fontWeight: 600 }}>Expiró</span>
        )}
      </div>

      <div style={{ padding: "18px 20px" }}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", marginBottom: 14,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 2 }}>
              {quote.client_name}
            </div>
            <div style={{ fontSize: 12, color: "#8fa3b1" }}>
              {SERVICE_LABELS[quote.service_key] || quote.service_key} ·
              📍 {quote.common_data?.address}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#163244" }}>
              {formatARS(parseFloat(quote.amount))}
            </div>
            <div style={{ fontSize: 11, color: "#8fa3b1" }}>
              Enviado {timeAgo(quote.created_at)}
            </div>
          </div>
        </div>

        {/* Descripción */}
        <div style={{
          background: "#f7fafc", borderRadius: 8, padding: "12px 14px",
          fontSize: 13, color: "#163244", lineHeight: 1.6,
          borderLeft: "3px solid #cddae4", marginBottom: 12,
        }}>
          {quote.description}
        </div>

        {/* Detalles */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          {quote.estimated_start_date && (
            <div style={{
              background: "#f0f5f8", borderRadius: 6, padding: "5px 12px",
              fontSize: 12, color: "#163244", display: "flex", gap: 6,
            }}>
              <span>📅</span>
              <span>Inicio: <strong>{new Date(quote.estimated_start_date).toLocaleDateString("es-AR")}</strong></span>
            </div>
          )}
          <div style={{
            background: quote.estimated_visit_required ? "#fff3e0" : "#f0f5f8",
            borderRadius: 6, padding: "5px 12px",
            fontSize: 12, color: quote.estimated_visit_required ? "#b06a00" : "#163244",
          }}>
            {quote.estimated_visit_required ? "🔍 Requiere visita previa" : "✓ Sin visita previa"}
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <button
            onClick={() => onEdit(quote)}
            style={{
              padding: "9px 20px", borderRadius: 8,
              border: "2px solid #163244", background: "transparent",
              color: "#163244", fontFamily: "Montserrat, sans-serif",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.target.style.background = "#163244"; e.target.style.color = "#fff"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = "#163244"; }}
          >
            ✏ Editar presupuesto
          </button>
        )}

        {quote.status === "accepted" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{
              background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8,
              padding: "10px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              🎉 ¡El cliente aceptó tu presupuesto! Coordiná el trabajo directamente.
            </div>
            {/* Datos de contacto del cliente */}
            <div style={{
              background: "#f0f5f8", border: "1.5px solid #cddae4", borderRadius: 8,
              padding: "14px 16px",
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: "#8fa3b1",
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
              }}>
                Contacto del cliente
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>👤</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#163244" }}>
                    {quote.client_name}
                  </span>
                </div>
                {quote.client_phone && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>📱</span>
                    <a
                      href={`tel:${quote.client_phone}`}
                      style={{ fontSize: 14, color: "#163244", fontWeight: 600, textDecoration: "none" }}
                    >
                      {quote.client_phone}
                    </a>
                  </div>
                )}
                {quote.client_email && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>✉️</span>
                    <a
                      href={`mailto:${quote.client_email}`}
                      style={{ fontSize: 14, color: "#163244", fontWeight: 600, textDecoration: "none" }}
                    >
                      {quote.client_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
            {/* Botón chat */}
            <button
              onClick={() => navigate(`/chat/${quote.request_id}`)}
              style={{
                marginTop: 10, padding: "10px 20px", borderRadius: 8,
                background: "#E7A432", border: "none", color: "#163244",
                fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                fontSize: 14, cursor: "pointer", display: "flex",
                alignItems: "center", gap: 8, width: "100%",
                justifyContent: "center",
              }}
            >
              💬 Abrir chat con el cliente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── New Quote Modal ────────────────────────────────────────────
// Carga las solicitudes asignadas al prestador (tipo quote, sin presupuesto enviado aun)
// y permite elegir desde un selector en lugar de tipear el request_id manualmente.
function NewQuoteModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    request_id: "", amount: "", description: "",
    estimated_visit_required: false, estimated_start_date: "",
  });
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Cargar solicitudes asignadas al prestador que no tienen presupuesto enviado
  useEffect(() => {
    setLoadingRequests(true);
    // Traer todas y filtrar en cliente — incluye notified (no respondio) y accepted (acepto pero no presupuesto aun)
    api.get("/matching/provider/requests")
      .then(res => {
        const quoteRequests = (res.data.requests || []).filter(r =>
          r.request_type === "quote" &&
          ["notified", "accepted"].includes(r.response_status)
        );
        setPendingRequests(quoteRequests);
        if (quoteRequests.length === 1) {
          setForm(f => ({ ...f, request_id: quoteRequests[0].request_id }));
        }
      })
      .catch(() => setPendingRequests([]))
      .finally(() => setLoadingRequests(false));
  }, []);

  const inputStyle = {
    padding: "10px 14px", borderRadius: 8, border: "1.5px solid #cddae4",
    fontSize: 14, fontFamily: "Montserrat, sans-serif", color: "#163244",
    background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, color: "#163244",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
  };

  const handleSave = async () => {
    if (!form.amount || !form.description || !form.request_id) {
      setError("Completá todos los campos requeridos.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/matching/provider/quotes", {
        request_id:               Number(form.request_id),
        amount:                   Number(form.amount),
        description:              form.description,
        estimated_visit_required: form.estimated_visit_required,
        estimated_start_date:     form.estimated_start_date || null,
      });
      onSave(res.data.quote || res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Error al enviar el presupuesto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.5)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "32px",
        width: "min(520px, 92vw)", maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 8px 40px rgba(22,50,68,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <h3 style={{ margin: 0, color: "#163244", fontSize: 18, fontWeight: 800 }}>
            Enviar presupuesto
          </h3>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20, color: "#8fa3b1", cursor: "pointer",
          }}>x</button>
        </div>

        {/* Selector de solicitud */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Solicitud *</label>
          {loadingRequests ? (
            <div style={{ color: "#8fa3b1", fontSize: 13, padding: "10px 0" }}>
              Cargando solicitudes...
            </div>
          ) : pendingRequests.length === 0 ? (
            <div style={{
              padding: "12px 14px", borderRadius: 8, background: "#f0f5f8",
              fontSize: 13, color: "#5c7a8a", border: "1.5px solid #cddae4",
            }}>
              No hay solicitudes de presupuesto pendientes.
            </div>
          ) : (
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.request_id}
              onChange={e => setForm(f => ({ ...f, request_id: e.target.value }))}
            >
              <option value="">Seleccioná una solicitud...</option>
              {pendingRequests.map(r => (
                <option key={r.match_id} value={r.request_id}>
                  #{r.request_id} — {SERVICE_LABELS[r.service_key] || r.service_key} — {r.client_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Monto (ARS) *</label>
          <input
            type="number" style={inputStyle} placeholder="Ej: 50000"
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Descripción del trabajo *</label>
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: "vertical", lineHeight: 1.6 }}
            placeholder="Describí el trabajo incluido en el presupuesto..."
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>
        <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Inicio estimado</label>
            <input
              type="date" style={inputStyle}
              value={form.estimated_start_date}
              onChange={e => setForm(f => ({ ...f, estimated_start_date: e.target.value }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>Requiere visita previa?</label>
            <div
              onClick={() => setForm(f => ({ ...f, estimated_visit_required: !f.estimated_visit_required }))}
              style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", paddingTop: 4 }}
            >
              <div style={{
                width: 44, height: 24, borderRadius: 12,
                background: form.estimated_visit_required ? "#163244" : "#c8d4dc",
                position: "relative", transition: "background 0.25s",
              }}>
                <div style={{
                  position: "absolute", top: 3,
                  left: form.estimated_visit_required ? 23 : 3,
                  width: 18, height: 18, borderRadius: "50%",
                  background: form.estimated_visit_required ? "#E7A432" : "#fff",
                  transition: "left 0.25s",
                }} />
              </div>
              <span style={{ fontSize: 13, color: "#163244", fontWeight: 600 }}>
                {form.estimated_visit_required ? "Sí" : "No"}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            marginBottom: 14, padding: "10px 14px", borderRadius: 8,
            background: "#fdecea", color: "#c0392b", fontSize: 13, fontWeight: 600,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "11px 22px", borderRadius: 8, border: "2px solid #e0e9ef",
            background: "transparent", color: "#5c7a8a",
            fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer",
          }}>Cancelar</button>
          <button
            onClick={handleSave}
            disabled={loading || !form.amount || !form.description || !form.request_id || pendingRequests.length === 0}
            style={{
              padding: "11px 28px", borderRadius: 8, background: "#163244",
              border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif",
              fontWeight: 700, fontSize: 14,
              cursor: loading || !form.amount || !form.request_id ? "not-allowed" : "pointer",
              opacity: !form.amount || !form.description || !form.request_id ? 0.6 : 1,
              display: "flex", alignItems: "center", gap: 8,
            }}>
            {loading ? (
              <span style={{
                width: 14, height: 14, border: "2px solid #fff",
                borderTopColor: "transparent", borderRadius: "50%",
                animation: "spin 0.7s linear infinite", display: "inline-block",
              }} />
            ) : "Enviar presupuesto"}
          </button>
        </div>
      </div>
    </div>
  );
}
// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderQuotesPage() {
  const [quotes,      setQuotes]      = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter,      setFilter]      = useState("all");
  const [editing,     setEditing]     = useState(null);
  const [newModal,    setNewModal]    = useState(false);
  const [toast,       setToast]       = useState(null);

  // Cargar presupuestos desde GET /matching/provider/quotes
  useEffect(() => {
    setLoadingData(true); setLoadError("");
    api.get("/matching/provider/quotes")
      .then(res => setQuotes(res.data.quotes || []))
      .catch(err => {
        console.error("Error cargando presupuestos:", err);
        setLoadError("No se pudieron cargar los presupuestos.");
      })
      .finally(() => setLoadingData(false));
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEdit = (quote) => setEditing(quote);

  const handleSaveEdit = (updated) => {
    setQuotes(qs => qs.map(q => q.id === updated.id ? updated : q));
    showToast("Presupuesto actualizado correctamente.");
  };

  const handleNewQuote = (newQuote) => {
    setQuotes(qs => [newQuote, ...qs]);
    showToast("¡Presupuesto enviado!");
  };

  const FILTERS = [
    { id: "all",       label: "Todos",     count: quotes.length },
    { id: "submitted", label: "Enviados",  count: quotes.filter(q => q.status === "submitted").length },
    { id: "accepted",  label: "Aceptados", count: quotes.filter(q => q.status === "accepted").length },
    { id: "rejected",  label: "Rechazados",count: quotes.filter(q => q.status === "rejected").length },
  ];

  const filtered = filter === "all" ? quotes : quotes.filter(q => q.status === filter);

  const stats = {
    total:    quotes.length,
    accepted: quotes.filter(q => q.status === "accepted").length,
    pending:  quotes.filter(q => q.status === "submitted").length,
    totalARS: quotes.filter(q => q.status === "accepted").reduce((s, q) => s + parseFloat(q.amount || 0), 0),
  };

  return (
    <>
      <div style={{ fontFamily: "Montserrat, sans-serif", maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800, color: "#163244" }}>
              Mis presupuestos
            </h1>
            <p style={{ margin: 0, color: "#8fa3b1", fontSize: 14 }}>
              Seguí el estado de cada presupuesto enviado.
            </p>
          </div>
          <button
            onClick={() => setNewModal(true)}
            style={{
              padding: "11px 20px", borderRadius: 8, background: "#163244",
              border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif",
              fontWeight: 700, fontSize: 14, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            + Nuevo presupuesto
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12, marginBottom: 20,
        }}>
          {[
            { label: "Enviados",    value: stats.pending,              color: "#b06a00" },
            { label: "Aceptados",   value: stats.accepted,             color: "#1b7f4e" },
            { label: "Facturado",   value: formatARS(stats.totalARS),  color: "#163244" },
          ].map(s => (
            <div key={s.label} style={{
              background: "#fff", borderRadius: 12, padding: "16px 18px",
              boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#8fa3b1", fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 20,
          background: "#fff", borderRadius: 12, padding: 6,
          boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
        }}>
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                flex: 1, padding: "9px 8px", borderRadius: 8, border: "none",
                background: filter === f.id ? "#163244" : "transparent",
                color: filter === f.id ? "#fff" : "#5c7a8a",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: filter === f.id ? 700 : 500,
                fontSize: 13, cursor: "pointer", transition: "all 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {f.label}
              <span style={{
                background: filter === f.id ? "#E7A432" : "#e0e9ef",
                color: filter === f.id ? "#163244" : "#5c7a8a",
                borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 800,
              }}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        {/* Loading / error */}
        {loadingData && (
          <div style={{ textAlign: "center", padding: "48px", color: "#8fa3b1" }}>
            <span style={{ display: "inline-block", width: 24, height: 24, border: "3px solid #cddae4", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br />
            Cargando presupuestos...
          </div>
        )}
        {loadError && !loadingData && (
          <div style={{ textAlign: "center", padding: "32px", color: "#c0392b", fontSize: 14 }}>
            ⚠ {loadError}
          </div>
        )}

        {/* Cards */}
        {!loadingData && !loadError && (filtered.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "48px 20px", background: "#fff",
            borderRadius: 14, border: "2px dashed #e0e9ef", color: "#8fa3b1",
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>No hay presupuestos en esta categoría</div>
          </div>
        ) : (
          filtered.map(q => (
            <QuoteCard key={q.id} quote={q} onEdit={handleEdit}  />
          ))
        ))}
      </div>

      {/* Modals */}
      {editing && (
        <EditModal quote={editing} onSave={handleSaveEdit} onClose={() => setEditing(null)} />
      )}
      {newModal && (
        <NewQuoteModal onSave={handleNewQuote} onClose={() => setNewModal(false)} />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#163244", color: "#fff", padding: "12px 24px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          borderLeft: "4px solid #E7A432",
        }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </>
  );
}
